"use client"

import type React from "react"
import { useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FileUp, Download, ImageIcon, Loader2, AlertCircle, Trash2, ArrowRight } from "lucide-react"
import { toast } from "sonner"
import { Progress } from "@/components/ui/progress"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import Image from "next/image"
import { API_URL, API_KEY } from "@/lib/api-config"

// Maximum file size: 50MB
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024

type ProcessingStatus = "idle" | "creating-job" | "uploading" | "processing" | "completed" | "error"

interface ProcessingResult {
  downloadUrl: string
  fileName: string
  originalSize: number
  processedSize: number
  compressionRatio: number
  spaceSavedPercent: number
  width: number
  height: number
  format: string
}

const SUPPORTED_FORMATS = [
  { value: "JPEG", label: "JPEG" },
  { value: "PNG", label: "PNG" },
  { value: "WEBP", label: "WebP" },
]

export function ImageCompressor() {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [quality, setQuality] = useState(80)
  const [outputFormat, setOutputFormat] = useState("JPEG")
  const [width, setWidth] = useState(0)
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true)

  const [status, setStatus] = useState<ProcessingStatus>("idle")
  const [uploadProgress, setUploadProgress] = useState(0)
  const [result, setResult] = useState<ProcessingResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please upload an image file")
      return
    }

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB limit`)
      return
    }

    setFile(selectedFile)
    setPreview(URL.createObjectURL(selectedFile))
    setResult(null)
    setError(null)
    setStatus("idle")
  }

  const clearFile = () => {
    if (preview) {
      URL.revokeObjectURL(preview)
    }
    setFile(null)
    setPreview(null)
    setResult(null)
    setError(null)
    setStatus("idle")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const compressImage = async () => {
    if (!file) return

    setStatus("creating-job")
    setError(null)
    setUploadProgress(0)

    try {
      // Step 1: Create job and get upload URL
      const createResponse = await fetch(`${API_URL}/api/v1/image/compress`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_KEY,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSizeBytes: file.size,
          quality,
          outputFormat,
          width: width > 0 ? width : null,
          height: null,
          maintainAspectRatio,
        }),
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to create compression job")
      }

      const { jobId, uploadUrl } = await createResponse.json() as { jobId: string; uploadUrl: string }

      // Step 2: Upload image
      setStatus("uploading")

      const xhr = new XMLHttpRequest()

      await new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            setUploadProgress((e.loaded / e.total) * 100)
          }
        })

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error("Upload failed"))
          }
        })

        xhr.addEventListener("error", () => reject(new Error("Upload failed")))

        xhr.open("PUT", uploadUrl)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
      })

      // Step 3: Process image
      setStatus("processing")

      const processResponse = await fetch(`${API_URL}/api/v1/image/compress/${jobId}/process`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
        },
      })

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to compress image")
      }

      const processResult = await processResponse.json() as {
        downloadUrl: string
        fileName: string
        originalSizeBytes: number
        processedSizeBytes: number
        compressionRatio: number
        spaceSavedPercent: number
        dimensions: { width: number; height: number }
        outputFormat: string
      }

      setResult({
        downloadUrl: processResult.downloadUrl,
        fileName: processResult.fileName,
        originalSize: processResult.originalSizeBytes,
        processedSize: processResult.processedSizeBytes,
        compressionRatio: processResult.compressionRatio,
        spaceSavedPercent: processResult.spaceSavedPercent,
        width: processResult.dimensions.width,
        height: processResult.dimensions.height,
        format: processResult.outputFormat,
      })
      setStatus("completed")
      toast.success(`Image compressed! Saved ${processResult.spaceSavedPercent.toFixed(1)}%`)
    } catch (err) {
      setStatus("error")
      const message = err instanceof Error ? err.message : "An error occurred"
      setError(message)
      toast.error(message)
    }
  }

  const downloadImage = () => {
    if (!result?.downloadUrl) return
    window.open(result.downloadUrl, "_blank")
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const isProcessing = status === "creating-job" || status === "uploading" || status === "processing"

  return (
    <Card>
      <CardHeader>
        <CardTitle>Image Compressor</CardTitle>
        <CardDescription>
          Compress and optimize images for web. Supports JPEG, PNG, and WebP output formats.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Controls */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Upload Image</Label>
              <div className="flex items-center gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={isProcessing}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                  disabled={isProcessing}
                >
                  <FileUp className="h-4 w-4 mr-2" />
                  {file ? "Change File" : "Select Image"}
                </Button>
                {file && (
                  <Button variant="outline" size="icon" onClick={clearFile} disabled={isProcessing}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {file && (
                <p className="text-sm text-muted-foreground">
                  {file.name} ({formatFileSize(file.size)})
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Output Format</Label>
              <Select value={outputFormat} onValueChange={setOutputFormat} disabled={isProcessing}>
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_FORMATS.map((format) => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quality: {quality}%</Label>
              <Slider
                value={[quality]}
                min={10}
                max={100}
                step={5}
                onValueChange={(value) => setQuality(value[0])}
                disabled={isProcessing}
              />
              <p className="text-xs text-muted-foreground">Lower quality = smaller file size</p>
            </div>

            <div className="space-y-2">
              <Label>Resize Width: {width === 0 ? "Original" : `${width}px`}</Label>
              <Slider
                value={[width]}
                min={0}
                max={2000}
                step={50}
                onValueChange={(value) => setWidth(value[0])}
                disabled={isProcessing}
              />
              <p className="text-xs text-muted-foreground">0 = keep original size</p>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={maintainAspectRatio}
                onCheckedChange={setMaintainAspectRatio}
                id="aspect-ratio"
                disabled={isProcessing}
              />
              <Label htmlFor="aspect-ratio">Maintain aspect ratio</Label>
            </div>

            {/* Progress */}
            {isProcessing && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">
                    {status === "creating-job" && "Creating job..."}
                    {status === "uploading" && `Uploading... ${Math.round(uploadProgress)}%`}
                    {status === "processing" && "Compressing image..."}
                  </span>
                </div>
                {status === "uploading" && <Progress value={uploadProgress} />}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <Button
              onClick={compressImage}
              disabled={!file || isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Compress Image
                </>
              )}
            </Button>
          </div>

          {/* Preview */}
          <div className="flex flex-col items-center justify-center border rounded-lg p-4 min-h-[300px]">
            {preview ? (
              <div className="flex flex-col items-center gap-2 w-full">
                <div className="border rounded overflow-hidden bg-background/50 max-w-full">
                  <Image
                    src={preview}
                    alt="Preview"
                    width={400}
                    height={300}
                    style={{ maxWidth: "100%", maxHeight: "250px", objectFit: "contain" }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">Original Preview</p>
              </div>
            ) : (
              <div className="flex flex-col items-center text-muted-foreground">
                <ImageIcon className="h-16 w-16 mb-2" />
                <p>Upload an image to preview</p>
              </div>
            )}
          </div>
        </div>

        {/* Result */}
        {status === "completed" && result && (
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="space-y-2">
                <p className="font-medium text-green-800 dark:text-green-200">Image Compressed Successfully!</p>
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <span>{formatFileSize(result.originalSize)}</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="font-medium">{formatFileSize(result.processedSize)}</span>
                  <span className="text-green-700 dark:text-green-300">
                    ({result.spaceSavedPercent.toFixed(1)}% saved)
                  </span>
                </div>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {result.width}x{result.height} • {result.format}
                </p>
              </div>
              <Button onClick={downloadImage}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
